import { createNode, type WorkflowNode } from './model';

export const GRID_SIZE = 24;
export const NODE_WIDTH = GRID_SIZE * 9;
export const NODE_HEIGHT = GRID_SIZE * 6;
export const CAMERA_NODE_HEIGHT = GRID_SIZE * 10;
const NODE_CLEARANCE = GRID_SIZE * 2;

export function nodeWidth(node: WorkflowNode) {
  return Number.isFinite(node.width) && node.width > 0 ? node.width : NODE_WIDTH;
}

export function nodeHeight(node: WorkflowNode) {
  const persisted = Number.isFinite(node.height) && node.height > 0 ? node.height : 0;
  if (node.kind === 'camera' || node.kind === 'display') return Math.max(persisted, CAMERA_NODE_HEIGHT);
  if (node.kind === 'split' || node.kind === 'mediaSplit' || node.kind === 'group') {
    const outputCount = node.kind === 'mediaSplit'
      ? 2
      : node.group
        ? node.group.outputs.length
        : Number(node.config.outputs ?? '2') || 2;
    return Math.max(persisted, NODE_HEIGHT, Math.ceil((120 + outputCount * 36) / GRID_SIZE) * GRID_SIZE);
  }
  if (node.inputPorts?.length) {
    return Math.max(persisted, NODE_HEIGHT, Math.ceil((120 + node.inputPorts.length * 24) / GRID_SIZE) * GRID_SIZE);
  }
  return Math.max(persisted, NODE_HEIGHT);
}

export function nodesAreTooClose(node: WorkflowNode, other: WorkflowNode, x = node.x, y = node.y) {
  return (
    x - NODE_CLEARANCE < other.x + nodeWidth(other) &&
    x + nodeWidth(node) + NODE_CLEARANCE > other.x &&
    y - NODE_CLEARANCE < other.y + nodeHeight(other) &&
    y + nodeHeight(node) + NODE_CLEARANCE > other.y
  );
}

export function hasNodeCollision(nodes: WorkflowNode[], movingIDs: Set<string>) {
  const moving = nodes.filter((node) => movingIDs.has(node.id));
  const stationary = nodes.filter((node) => !movingIDs.has(node.id));
  return moving.some((node, index) =>
    stationary.some((other) => nodesAreTooClose(node, other)) ||
    moving.slice(index + 1).some((other) => nodesAreTooClose(node, other)),
  );
}

export function findOpenNodePosition(nodes: WorkflowNode[], x: number, y: number, kind: WorkflowNode['kind']) {
  const candidate = createNode(kind, 'position-check', x, y);
  const snap = (value: number) => Math.round(value / GRID_SIZE) * GRID_SIZE;
  for (let row = 0; row < 2000 / GRID_SIZE; row += 1) {
    for (let column = 0; column < 3200 / GRID_SIZE; column += 1) {
      const nextX = snap(x) + column * GRID_SIZE;
      const nextY = snap(y) + row * GRID_SIZE;
      if (!nodes.some((node) => nodesAreTooClose(candidate, node, nextX, nextY))) {
        return { x: nextX, y: nextY };
      }
    }
  }
  return { x: snap(x), y: snap(y) };
}
